import React from 'react';
import PropTypes from 'prop-types';
import { createTheme, StylesProvider } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import { functional, neutral0, promo700 } from '@fever/ui';
import { zIndex as sharedZIndex } from 'styles/zIndex';

// Mirror of fever/ui `--fw-z-*` tokens so MUI overlays stack predictably with
// fever-component overlays. See src/styles/zIndex.js for the full scale.
const muiZIndex = {
    drawer: sharedZIndex.drawer,
    modal: sharedZIndex.modal,
    tooltip: sharedZIndex.tooltip,
    snackbar: sharedZIndex.toast
};

const commonConfig = {
    zIndex: muiZIndex,
    typography: {
        fontFamily: 'var(--fw-font-sans)',
        h1: {
            letterSpacing: '-1.5px'
        },
        h2: {
            letterSpacing: '-0.5px;'
        },
        h3: {
            letterSpacing: 'normal'
        },
        h4: {
            letterSpacing: 'normal'
        },
        h5: {
            letterSpacing: '0.18px'
        },
        h6: {
            letterSpacing: '0.15px'
        },
        subtitle1: {
            letterSpacing: '0.15px'
        },
        subtitle2: {
            letterSpacing: '0.1px'
        },
        body1: {
            letterSpacing: '0.5px'
        },
        body2: {
            letterSpacing: '0.25px'
        },
        button: {
            letterSpacing: '1.25px'
        },
        caption: {
            letterSpacing: '0.4px'
        }
    }
};

export const promoTheme = createTheme({
    palette: {
        type: 'light',
        primary: {
            main: functional,
            contrastText: neutral0
        },
        secondary: { main: promo700 }
    },
    ...commonConfig
});

export function promoThemeClient({ primaryColor, secondaryColor, textColor }) {
    const { fontFamily, ...variants } = commonConfig.typography;
    const mergeFontWeight = Object.keys(variants).reduce((obj, key) => {
        obj = { ...obj, [key]: { ...variants[key], fontWeight: 500, color: textColor } };
        return obj;
    }, {});
    return createTheme({
        palette: {
            type: 'light',
            primary: {
                main: primaryColor,
                contrastText: neutral0
            },
            secondary: { main: secondaryColor }
        },
        typography: {
            fontFamily,
            ...mergeFontWeight
        }
    });
}

function MuiThemeProvider({ children, theme, injectFirst = true }) {
    return (
        <ThemeProvider theme={theme}>
            <StylesProvider injectFirst={injectFirst}>{children}</StylesProvider>
        </ThemeProvider>
    );
}

MuiThemeProvider.propTypes = {
    children: PropTypes.node,
    theme: PropTypes.object
};

export default MuiThemeProvider;
